import React from 'react';

import Button from '../../../Button/Button';
import Dropdown from '../../../DropDown/Dropdown';
import Icon from '../../../Icon2/Icon';
import { CustomDropdownOption, SetSortByData } from '../../utils/types';
import { SPACE_ASSETS_SORT, SPACE_ASSETS_THUMBNAIL_SORT_OPTIONS } from '../../utils/constants';

export const getThumbnailSortDropdownOptions = (
  sortById: string,
  sortingDirection: string,
  setSortByData: SetSortByData,
  responsiveSortList: boolean = false
): CustomDropdownOption[] => {
  const options = responsiveSortList
    ? SPACE_ASSETS_THUMBNAIL_SORT_OPTIONS
    : SPACE_ASSETS_THUMBNAIL_SORT_OPTIONS.slice(0, 6);
  return options.map((option) => {
    const highlightItem = responsiveSortList
      ? (option.isSortOrder && option.value === sortingDirection) || (!option.isSortOrder && option.value === sortById)
      : option.value === sortById;
    return {
      key: option.value,
      label: option.isSortOrder ? (
        <div className="flex-justify flex-v-center">
          <span className="sort-order-label">{option.label}</span>
          <Icon
            icon={
              option.value === SPACE_ASSETS_SORT.ASC
                ? 'SortAscending'
                : option.value === SPACE_ASSETS_SORT.DESC
                  ? 'SortDescending'
                  : 'NoSortArrow'
            }
            size="small"
            version="v2"
          />
        </div>
      ) : (
        option.label
      ),
      textLabel: option.label,
      value: option.value,
      action: () => {
        setSortByData((prev) => {
          // On responsive sort list, only update the sortingDirection, if User clicks 'asc', 'desc' if the option is a sort order,
          // so persisting the id in this case. Since we show 'asc', 'desc' in same dropdown.
          const data = {
            ...prev,
            ...(!option.isSortOrder ? { id: option.value } : { id: prev.id, sortingDirection: option.value })
          };
          return data;
        });
      },
      default: option.value === sortById,
      ...(responsiveSortList && { highlightItem }),
      testId: `am-v2-space-assets-thumbnail-sort-dropdown-${option.value}-option`,
      className: `SpaceAssetsModalSort__dropdown-option`,
      disable: 'disable' in option ? option.disable : false
    };
  });
};

const sortOrderIconDefaultProps = {
  version: 'v2',
  width: 22
} as const;

const SpaceAssetsModalSort = (props) => {
  const {
    sortByData,
    setSortByData,
    sortOptionsList,
    dropDownLabel,
    sortFor,
    sortOrderIconAdditionalProps = {},
    shouldDisabled,
    isResponsive
  } = props;
  const sortOrderIconCommonProps = {
    ...sortOrderIconDefaultProps,
    ...sortOrderIconAdditionalProps
  };
  const setSortOrder = (sortOrderValue: string) => {
    setSortByData((prev: { id: string; sortingDirection: string }) => ({
      ...prev,
      sortingDirection: sortOrderValue
    }));
  };

  return (
    <div className={`flex-center SpaceAssetsModalSort ${isResponsive ? 'SpaceAssetsModalSort--resp_screen' : ''}`}>
      {dropDownLabel && <p className="SpaceAssetsModalSort__label">{dropDownLabel}</p>}
      {isResponsive ? (
        <Dropdown
          testId={`${sortFor}-sort-icon-dropdown`}
          className="SpaceAssetsModalSort__dropdown--resp_screen"
          version={'v2'}
          type="click"
          dropDownPosition="left"
          list={sortOptionsList}
          closeAfterSelect={true}
          withArrow={true}
          highlightActive={true}
          isDisabled={shouldDisabled}
          variant={{
            name: 'group',
            dividerItemIndexes: [6],
            enableHighlightItem: true
          }}
        />
      ) : (
        <Dropdown
          testId={`${sortFor}-sort-dropdown`}
          className="SpaceAssetsModalSort__dropdown"
          version={sortOrderIconDefaultProps.version}
          type="select"
          dropDownType="primary"
          dropDownPosition="bottom"
          list={sortOptionsList}
          closeAfterSelect={true}
          canCloseOnClickOutside={true}
          withArrow={true}
          highlightActive={true}
          isDisabled={shouldDisabled}
        />
      )}
      {/* Sorting Order Button Icon */}
      {!isResponsive ? (
        sortByData.sortingDirection === SPACE_ASSETS_SORT.ASC ? (
          <Button
            testId={`${sortFor}-sort-asc-order-btn-icon`}
            size="small"
            buttonType="tertiary"
            className="SpaceAssetsModalSort__order-btn"
            onlyIcon={true}
            icon="SortingAsc"
            iconProps={{ ...sortOrderIconCommonProps }}
            onlyIconHoverColor={true}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setSortOrder(SPACE_ASSETS_SORT.DESC);
              }
            }}
            onClick={() => setSortOrder(SPACE_ASSETS_SORT.DESC)}
            disabled={shouldDisabled}
          />
        ) : (
          <Button
            testId={`${sortFor}-sort-desc-order-btn-icon`}
            size="small"
            buttonType="tertiary"
            className="SpaceAssetsModalSort__order-btn"
            onlyIcon={true}
            icon="SortingDesc"
            iconProps={{ ...sortOrderIconCommonProps }}
            onlyIconHoverColor={true}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setSortOrder(SPACE_ASSETS_SORT.ASC);
              }
            }}
            onClick={() => setSortOrder(SPACE_ASSETS_SORT.ASC)}
            disabled={shouldDisabled}
          />
        )
      ) : null}
    </div>
  );
};

export default SpaceAssetsModalSort;
